import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../components/Icon';

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Apagada por omisión: en un equipo compartido, mantener la sesión abierta
  // es una decisión que se toma a propósito, no una que se hereda.
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await login(email, password, rememberMe);
      // El destino lo decide el rol, igual que en el mockup de acceso.
      if (user.passwordChangeRequired) navigate('/cambiar-contrasena', { replace: true });
      else if (user.role === 'CLIENT') navigate('/portal', { replace: true });
      else navigate('/admin', { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError
        ? caught.message
        : 'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--space-4)' }}>
      <form className="card" onSubmit={handleSubmit} style={{ width: 'min(420px, 100%)' }} noValidate>
        <img
          src="/logo-buona-pizza.webp"
          alt="Buona Pizza Sartori"
          style={{ width: 170, height: 'auto', marginBottom: 'var(--space-3)' }}
        />
        <p style={{ color: 'var(--ink-soft)', marginTop: 0, marginBottom: 'var(--space-5)' }}>
          Ingresa con la cuenta que Buona Pizza creó para tu empresa.
        </p>

        {error && <div className="alert alert--error" role="alert">{error}</div>}

        <div className="field">
          <label htmlFor="email">Correo</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="password">Contraseña</label>
          <div className="password-field">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            {/* Ver lo escrito evita el error más común al entrar: una tecla de
                más en una contraseña que no se ve. */}
            <button
              type="button"
              className="password-field__toggle"
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((shown) => !shown)}
            >
              <Icon name={showPassword ? 'eyeOff' : 'eye'} size={18} />
            </button>
          </div>
        </div>

        <label
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'flex-start',
            marginBottom: 'var(--space-4)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(event) => setRememberMe(event.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            <strong>Mantener la sesión iniciada</strong>
            <span style={{ display: 'block', color: 'var(--ink-soft)', fontSize: 'var(--text-xs)', marginTop: 2 }}>
              Sólo en equipos de tu confianza. Sin esto, la sesión se cierra sola tras unas horas.
            </span>
          </span>
        </label>

        <button className="button button--primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Ingresando…' : 'Ingresar'}
        </button>
      </form>
    </main>
  );
}
